
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/toaster";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthProvider, RequireAuth } from "@/contexts/AuthContext";
import { PostHogIdentity } from "@/components/PostHogIdentity";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Freelancers from "@/pages/Freelancers";
import FreelancerDetails from "@/pages/FreelancerDetails";
import LinkedInPage from "@/pages/LinkedInPage";
import LinkedInReviewPage from "@/pages/linkedin/LinkedInReviewPage";
import LinkedInBatchReviewPage from "@/pages/linkedin/LinkedInBatchReviewPage";
import LinkedInLotePage from "@/pages/linkedin/LinkedInLotePage";
import WhatsAppAtualizacoesPage from "@/pages/WhatsAppAtualizacoesPage";
import Financeiro from "@/pages/Financeiro";
import NovaProposta from "@/pages/NovaProposta";
import PropostaDetalhe from "@/pages/PropostaDetalhe";
import EmpresasPage from "@/pages/EmpresasPage";
import EmpresaDetailPage from "@/pages/EmpresaDetailPage";
import Vagas from "@/pages/Vagas";
import VagaDetails from "@/pages/VagaDetails";
import Projetos from "@/pages/Projetos";
import ControleProjetos from "@/pages/ControleProjetos";
import Agencias from "@/pages/Agencias";
import AgenciaDetails from "@/pages/AgenciaDetails";
import Avaliacoes from "@/pages/Avaliacoes";
import AvaliacoesPerfilFreelancer from "@/pages/AvaliacoesPerfilFreelancer";
import EvaluationPage from "@/pages/EvaluationPage";
import FormulariosAvaliacao from "@/pages/FormulariosAvaliacao";
import Configuracoes from "@/pages/Configuracoes";
import GestaoPerfis from "@/pages/GestaoPerfis";
import RecomendacaoPesos from "@/pages/admin/RecomendacaoPesos";
import CompletudeDados from "@/pages/admin/CompletudeDados";
import RadarAgencias from "@/pages/RadarAgencias";
import Atividades from "@/pages/Atividades";
import Campanhas from "@/pages/Campanhas";
import DashboardAgencia from "@/pages/agencia/DashboardAgencia";
import AvaliacoesAgencia from "@/pages/agencia/AvaliacoesAgencia";
import GestaoUsuariosAgencia from "@/pages/agencia/GestaoUsuariosAgencia";
import NotFound from "@/pages/NotFound";
/* Agents Module
import ChatPage from "@/pages/agents/ChatPage";
import MatchPage from "@/pages/agents/MatchPage";
import PortfoliosPage from "@/pages/agents/PortfoliosPage";
import MonitoringPage from "@/pages/agents/MonitoringPage";
*/
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ROTAS_DA_VERSAO_NOVA, RotaDaVersaoNova } from "@/components/auth/RotaDaVersaoNova";
import { ErrorBoundary } from "@/components/ErrorBoundary";

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <PostHogIdentity />
          <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
            <BrowserRouter>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/auth" element={<Auth />} />
              <Route
                element={
                  <RequireAuth>
                    <AppLayout />
                  </RequireAuth>
                }
              >
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute requiredScreen="dashboard">
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/freelancers"
                  element={
                    <ProtectedRoute requiredScreen="freelancers">
                      <Freelancers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/freelancers/:id"
                  element={
                    <ProtectedRoute requiredScreen="freelancers">
                      <FreelancerDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/linkedin"
                  element={
                    <ProtectedRoute requiredScreen="linkedin">
                      <LinkedInPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/linkedin/jobs/:jobId/revisar"
                  element={
                    <ProtectedRoute requiredScreen="linkedin">
                      <LinkedInReviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/linkedin/lote/:batchId"
                  element={
                    <ProtectedRoute requiredScreen="linkedin">
                      <LinkedInLotePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/linkedin/lote/:batchId/revisar"
                  element={
                    <ProtectedRoute requiredScreen="linkedin">
                      <LinkedInBatchReviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/atualizacoes-whatsapp"
                  element={
                    <ProtectedRoute requiredScreen="atualizacoes_whatsapp">
                      <WhatsAppAtualizacoesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/financeiro"
                  element={
                    <ProtectedRoute requiredScreen="financeiro">
                      <Financeiro />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/financeiro/propostas/nova"
                  element={
                    <ProtectedRoute requiredScreen="financeiro">
                      <NovaProposta />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/financeiro/propostas/:id"
                  element={
                    <ProtectedRoute requiredScreen="financeiro">
                      <PropostaDetalhe />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/empresas"
                  element={
                    <ProtectedRoute requiredScreen="empresas">
                      <EmpresasPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/empresas/:id"
                  element={
                    <ProtectedRoute requiredScreen="empresas">
                      <EmpresaDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vagas"
                  element={
                    <ProtectedRoute requiredScreen="vagas">
                      <Vagas />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vagas/:id"
                  element={
                    <ProtectedRoute requiredScreen="vagas">
                      <VagaDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projetos"
                  element={
                    <ProtectedRoute requiredScreen="projetos">
                      <Projetos />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/controle-projetos"
                  element={
                    <ProtectedRoute requiredScreen="projetos">
                      <ControleProjetos />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agencias"
                  element={
                    <ProtectedRoute requiredScreen="agencias">
                      <Agencias />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agencias/:id"
                  element={
                    <ProtectedRoute requiredScreen="agencias">
                      <AgenciaDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/avaliacoes/pos-projeto"
                  element={
                    <ProtectedRoute requiredScreen="avaliacoes_pos_projeto">
                      <Avaliacoes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/avaliacoes/perfil-freelancer"
                  element={
                    <ProtectedRoute requiredScreen="avaliacoes_perfil_freelancer">
                      <AvaliacoesPerfilFreelancer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/avaliacoes/perfil-freelancer/:id"
                  element={
                    <ProtectedRoute requiredScreen="avaliacoes_perfil_freelancer">
                      <AvaliacoesPerfilFreelancer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/avaliacoes/perfil-freelancer/:groupId/avaliar"
                  element={
                    <ProtectedRoute requiredScreen="avaliacoes_perfil_freelancer">
                      <EvaluationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/avaliacoes/formularios"
                  element={
                    <ProtectedRoute requiredScreen="formularios_avaliacao">
                      <FormulariosAvaliacao />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/configuracoes"
                  element={
                    <ProtectedRoute requiredScreen="configuracoes">
                      <Configuracoes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/recomendacao-pesos"
                  element={
                    <ProtectedRoute requiredScreen="recomendacao_pesos">
                      <RecomendacaoPesos />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/completude-dados"
                  element={
                    <ProtectedRoute requiredScreen="completude_dados">
                      <CompletudeDados />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/radar-agencias"
                  element={
                    <ProtectedRoute requiredScreen="radar_agencias">
                      <RadarAgencias />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/gestao-perfis"
                  element={
                    <ProtectedRoute requiredScreen="gestao_perfis_usuarios">
                      <GestaoPerfis />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/atividades"
                  element={
                    <ProtectedRoute requiredScreen="atividades">
                      <Atividades />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/campanhas"
                  element={
                    <ProtectedRoute requiredScreen="campanhas">
                      <Campanhas />
                    </ProtectedRoute>
                  }
                />
                {/* Agência Routes */}
                <Route
                  path="/agencia/dashboard"
                  element={
                    <ProtectedRoute requiredScreen="dashboard_agencia">
                      <DashboardAgencia />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agencia/avaliacoes"
                  element={
                    <ProtectedRoute requiredScreen="avaliacoes_agencia">
                      <AvaliacoesAgencia />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agencia/usuarios"
                  element={
                    <ProtectedRoute requiredScreen="gestao_usuarios_agencia">
                      <GestaoUsuariosAgencia />
                    </ProtectedRoute>
                  }
                />
                {/* Agents Module Routes */}
                {/*
                <Route
                  path="/agents/chat"
                  element={
                    <ProtectedRoute requiredScreen="agents_chat">
                      <ChatPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agents/match"
                  element={
                    <ProtectedRoute requiredScreen="agents_match">
                      <MatchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agents/portfolios"
                  element={
                    <ProtectedRoute requiredScreen="agents_portfolios">
                      <PortfoliosPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agents/monitoring"
                  element={
                    <ProtectedRoute requiredScreen="agents_monitoring">
                      <MonitoringPage />
                    </ProtectedRoute>
                  }
                /> */}
                {ROTAS_DA_VERSAO_NOVA.map(([path, destino]) => (
                  <Route key={path} path={path} element={<RotaDaVersaoNova to={destino} />} />
                ))}
                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            {/* Dentro do Router: toasts podem ter ação com <Link> (ex.: "Ver vaga"). */}
            <Toaster />
            </BrowserRouter>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
